/**
 * NotificationsContext
 * ────────────────────
 * Lifted out of UIContext (Feb 2026) so the 30s / 120s polling cycle
 * stops triggering a render walk through the 88k-line Dashboard.
 *
 * Architecture: split into two contexts so consumers can choose what
 * they subscribe to.
 *
 *   • NotificationsStateContext  — { notifications, unreadCount,
 *                                    showNotifications }
 *     Value changes on every poll → only the *bell* and *manager
 *     dialog* should subscribe.
 *
 *   • NotificationsActionsContext — { setNotifications, setUnreadCount,
 *                                     setShowNotifications,
 *                                     fetchNotifications,
 *                                     dismissNotification }
 *     All callbacks are useState setters or useCallback([]) — value
 *     is stable for the provider's entire lifetime.  Dashboard / any
 *     ambient consumer subscribes to *this* so it can call
 *     fetchNotifications() / setNotifications() without ever
 *     re-rendering when the poll comes back.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationsStateContext = createContext(null);
const NotificationsActionsContext = createContext(null);

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  // Ref-mirror so fetchNotifications can compare without re-creating
  // its useCallback on every state change.
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      const data = response?.data || {};
      const nextNotifs = data.notifications || [];
      const nextUnread = data.unread_count || 0;

      // Reference-preserving setState: only swap arrays when something
      // the UI actually reads (id + read flag) changed.  Same trick the
      // pre-extraction code used inside App.js fetchNotifications.
      setNotifications((prev) => {
        if (prev.length !== nextNotifs.length) return nextNotifs;
        for (let i = 0; i < prev.length; i += 1) {
          if (prev[i].id !== nextNotifs[i].id) return nextNotifs;
          if (prev[i].read !== nextNotifs[i].read) return nextNotifs;
        }
        return prev;
      });
      setUnreadCount((prev) => (prev === nextUnread ? prev : nextUnread));
    } catch (error) {
      // Background poll failures should not nag the user — log only.
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const dismissNotification = useCallback(async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/dismiss`);
      fetchNotifications();
      toast.success('Notification dismissed');
    } catch (error) {
      console.error('dismissNotification failed:', error?.response?.status, error?.message);
      toast.error(`Failed to dismiss notification${error?.response?.status ? ` (${error.response.status})` : ''}`);
    }
  }, [fetchNotifications]);

  // ── Mar 2026 — Provider-owned polling ──────────────────────────
  // Previously the legacy `App.js` Dashboard owned the polling loop
  // and called `fetchNotifications()` on a 30s/120s cycle.  V2 routes
  // never mount that Dashboard, so the notification bell sat empty
  // for users who never bounced through `/dashboard` — the user
  // explicitly reported "in V2 preview I don't see any messages or
  // notifications, but I do see them in legacy."
  //
  // Owning the poll here makes the context self-sufficient for any
  // host (V2, legacy, future shells).  Legacy's existing call site
  // becomes redundant but is harmless (idempotent fetch).
  const { user } = useAuth() || {};
  useEffect(() => {
    if (!user) return undefined;
    // Initial fetch on auth.
    fetchNotifications();
    // 30s cycle when the tab is visible — matches legacy behavior
    // and keeps the bell live without hammering the API.  Pauses
    // automatically when the tab is hidden.
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  // Actions value — memoised ONCE.  Every member is either a useState
  // setter (stable identity) or a useCallback([]) function.  Consumers
  // of useNotificationsActions therefore never re-render when state
  // changes.
  const actions = useMemo(() => ({
    setNotifications,
    setUnreadCount,
    setShowNotifications,
    fetchNotifications,
    dismissNotification,
    // Helper used by App.js for the WebSocket urgent_waitlist clear
    // path — keeps internal data + counter in sync.
    notificationsRef,
  }), [fetchNotifications, dismissNotification]);

  const stateValue = useMemo(() => ({
    notifications,
    unreadCount,
    showNotifications,
  }), [notifications, unreadCount, showNotifications]);

  return (
    <NotificationsActionsContext.Provider value={actions}>
      <NotificationsStateContext.Provider value={stateValue}>
        {children}
      </NotificationsStateContext.Provider>
    </NotificationsActionsContext.Provider>
  );
};

export const useNotificationsState = () => {
  const ctx = useContext(NotificationsStateContext);
  if (!ctx) {
    throw new Error('useNotificationsState must be used within NotificationsProvider');
  }
  return ctx;
};

export const useNotificationsActions = () => {
  const ctx = useContext(NotificationsActionsContext);
  if (!ctx) {
    throw new Error('useNotificationsActions must be used within NotificationsProvider');
  }
  return ctx;
};
